/*
 * Bridges Rebuy recommendation cards into the theme's native UI.
 *
 * Rebuy renders its product cards via a Vue template but does not touch Dawn's
 * cart drawer / header pill, nor does it use the theme's slider. This script:
 *   1. Mounts the theme's Splide slider over Rebuy's rendered card list.
 *   2. Opens + re-renders the cart drawer after a Rebuy add.
 *   3. Publishes cartUpdate so cart-icon-sync refreshes the header pill.
 *   4. Swaps each Rebuy card's icon to "in cart" when its product is in cart,
 *      mirroring how component-cart-icon.liquid decides `in_cart` (by product id).
 */
(function () {
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // Matches the markup of snippets/icon-in-cart.liquid.
  var IN_CART_SVG =
    '<svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M12.2615 20.8075L10.3428 11.75H26.1253C26.939 11.75 27.5353 12.5138 27.3378 13.3038L25.6528 20.0438C25.3965 21.0675 24.5265 21.82 23.4765 21.925L14.9565 22.7775C13.6865 22.9038 12.5253 22.055 12.2615 20.8075Z" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M10.3425 11.75L9.53 8H7.375" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M24.3862 27.0838C24.1337 27.0838 23.9287 27.2888 23.9312 27.5413C23.9312 27.7938 24.1362 27.9988 24.3887 27.9988C24.6412 27.9988 24.8462 27.7938 24.8462 27.5413C24.8449 27.2888 24.6399 27.0838 24.3862 27.0838" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M13.8712 27.0829C13.6187 27.0829 13.4137 27.2879 13.4162 27.5404C13.4137 27.7941 13.6199 27.9991 13.8724 27.9991C14.1249 27.9991 14.3299 27.7941 14.3299 27.5416C14.3299 27.2879 14.1249 27.0829 13.8712 27.0829" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<rect x="21.5" y="4.5" width="11" height="11" rx="5.5" fill="#089E00"/>' +
    '<rect x="21.5" y="4.5" width="11" height="11" rx="5.5" stroke="white"/>' +
    '<path d="M29.5003 8.33203L26.3777 11.6654L24.917 9.9987" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  // window.Shopify.routes.root always ends with a trailing slash ('/' or
  // '/en-gb/'), so these concatenations stay correctly formed.
  var cartUrl = `${root}cart.js`;
  var sectionsUrl = `${root}?sections=cart-drawer,cart-icon-bubble`;

  var refreshing = false;

  // Mark every Rebuy card whose product is already in the cart.
  function markInCartCards(cart) {
    if (!cart || !cart.items) return;
    var productIds = new Set(cart.items.map((item) => String(item.product_id)));

    document.querySelectorAll('.rebuy-product-block').forEach((block) => {
      var match = (block.className.match(/product-id-(\d+)/) || [])[1];
      if (!match) return;

      var button = block.querySelector('.swf-cart-btn');
      if (!button) return;

      if (productIds.has(match)) {
        if (button.classList.contains('swf-cart-btn--in-cart')) return;
        var svg = button.querySelector('svg:not(.spinner)');
        if (svg) svg.outerHTML = IN_CART_SVG;
        button.classList.add('swf-cart-btn--in-cart');
      }
    });
  }

  async function refreshCartUi() {
    if (refreshing) return;
    refreshing = true;
    try {
      // Re-render + open the drawer.
      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        var sections = await (await fetch(sectionsUrl)).json();
        drawer.classList.remove('is-empty');
        drawer.renderContents({ sections: sections });
      }

      var cart = await (await fetch(cartUrl)).json();

      // Keep the header pill in sync (cart-icon-sync listens for this).
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'rebuy', cartData: cart });
      }

      markInCartCards(cart);
    } catch (error) {
      console.error('[rebuy] failed to sync cart UI', error);
    } finally {
      refreshing = false;
    }
  }

  // Reflect existing cart state whenever Rebuy (re)renders its cards.
  async function markFromCart() {
    try {
      var cart = await (await fetch(cartUrl)).json();
      markInCartCards(cart);
    } catch (error) {
      /* non-fatal */
    }
  }

  /* ----- Splide slider ----- */

  // Mount the theme's Splide slider over each Rebuy card list once it exists.
  function mountSliders() {
    if (typeof Splide === 'undefined') return;

    document.querySelectorAll('.rebuy-recommendations.splide').forEach((el) => {
      if (el.splideInstance) return;
      // Wait until Rebuy's Vue has rendered the slides.
      if (!el.querySelector('.splide__list > .splide__slide')) return;

      // `slide` type (no clones) keeps the DOM stable for Rebuy's Vue layer.
      var splide = new Splide(el, {
        type: 'slide',
        perPage: 4,
        perMove: 1,
        gap: '24px',
        arrows: true,
        pagination: false,
        speed: 500,
        easing: 'ease',
        breakpoints: {
          1023: { perPage: 3 },
          749: { perPage: 2, gap: '12px' },
          479: { perPage: 2, gap: '10px' },
        },
      });

      splide.mount();
      el.splideInstance = splide;

      // Reflect existing cart state on the freshly rendered cards.
      markFromCart();
    });
  }

  function watchForSlider() {
    var container = document.querySelector('[data-rebuy-id]');
    if (!container) return;

    var scheduled = false;
    var observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        mountSliders();
      });
    });

    observer.observe(container, { childList: true, subtree: true });
    mountSliders();
  }

  function start() {
    if (typeof Splide === 'undefined') {
      // Splide is loaded (deferred) from theme.liquid — wait for it.
      var splideScript = document.querySelector('script[src*="splide"]');
      if (splideScript) {
        splideScript.addEventListener('load', watchForSlider, { once: true });
      } else {
        window.addEventListener('load', watchForSlider, { once: true });
      }
    } else {
      watchForSlider();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('rebuy:cart.add', refreshCartUi);
  document.addEventListener('rebuy:widget.show', markFromCart);
  document.addEventListener('rebuy:cart.change', markFromCart);
})();
